import { useSession } from 'next-auth/react';
import pluralize from 'pluralize';
import React from 'react';
import { useRecoilValue } from 'recoil';

import { ModeIcon } from '@/components/mode-icon';
import { EmissionsBadge } from '@/components/modules/emissions/badge';
import { Text } from '@/components/text';
import { DISTANCE_UNITS, EVENT_TYPE, UNIT_CO2, USER_ACCESS_FEATURES } from '@/lib/constants';
import { SegmentLinkModel } from '@/lib/models/segment/types';
import { getLocationByIdState } from '@/state/locations';
import { canAccessFeature } from '@/utils/auth';
import { formatDistance, formatDuration, formatFrequency } from '@/utils/format';
import { getPreferredIdentifier } from '@/utils/helpers';
import { getTransportColor } from '@/utils/helpers/get-transport-color';
import { tv } from '@/utils/styles';

interface DirectionsPrimaryProps {
  segment: SegmentLinkModel;
}

const DirectionsPrimary = ({ segment }: DirectionsPrimaryProps) => {
  const { data: session } = useSession();
  const canSeeEmissions = canAccessFeature(USER_ACCESS_FEATURES.EMISSIONS, session);

  const { routeType, durationSeconds, distanceKm, frequency, events, fromId, toId, emissions } = segment;
  const fromPort = useRecoilValue(getLocationByIdState(fromId));
  const toPort = useRecoilValue(getLocationByIdState(toId));
  const originCode = getPreferredIdentifier(fromPort?.identifiers)?.value;
  const destinationCode = toPort?.identifiers ? getPreferredIdentifier(toPort.identifiers)?.value : '';
  const color = getTransportColor(routeType);
  const stopCount = events.length ? events[0].filter((event) => event.type === EVENT_TYPE.STOP).length : 0;

  const { base, modeBar, primaryText, subText, subTextLight } = styles();

  return (
    <div className={base()}>
      <div className={modeBar()} style={{ '--mode-bar-background': color } as React.CSSProperties}>
        <ModeIcon mode={routeType} size="sm" />
      </div>
      <div className="pl-8 pr-4">
        <Text className={primaryText()}>
          <b>{originCode}</b> {fromPort?.name}
        </Text>
        <div className="py-3">
          <Text className={subTextLight()}>{stopCount > 0 && `${stopCount} ${pluralize('stop', stopCount)}`}</Text>
        </div>
        <Text className={primaryText()}>
          <b>{destinationCode}</b> {toPort?.name}
        </Text>
      </div>
      <div>
        <Text className={subText()}>
          <b className="font-semiBold">{formatDuration(durationSeconds)}</b>
        </Text>
        {frequency?.perHour ? <p className={subTextLight()}>{formatFrequency(frequency.perHour)}</p> : null}
      </div>
      <div className="hidden xl:block">
        <Text className={subText()}>{formatDistance(distanceKm)}</Text>
        <Text className={subTextLight()}>{formatDistance(distanceKm, DISTANCE_UNITS.MI)}</Text>
      </div>
      <div className="hidden xl:block">
        {canSeeEmissions ? (
          <EmissionsBadge emissionStats={emissions} />
        ) : (
          <Text className={subText({ className: 'blur-[3px]' })}>N/A {UNIT_CO2}</Text>
        )}
      </div>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'relative grid grid-cols-[1fr_0.5fr] items-center p-4 leading-snug md:grid-cols-[var(--route-card-grid-cols-md)] xl:grid-cols-[var(--route-card-grid-cols-xl)]',
    modeBar:
      "absolute bottom-4 left-4 top-4 flex w-5 before:absolute before:bottom-0 before:left-0 before:right-0 before:top-0 before:z-0 before:rounded-[10px] before:bg-[var(--mode-bar-background)] before:opacity-60 before:content-[''] [&>_*]:relative [&>_*]:z-3",
    primaryText: 'm-0 text-sm text-text-primary [&_b]:text-baseSm [&_b]:font-semibold',
    subText: 'm-0 text-sm text-text-primary',
    subTextLight: 'm-0 text-sm text-text-secondary',
  },
});

export { DirectionsPrimary };
